import { WalletConfig } from "@/types/DocumentTemplate";
import { getStripAspect } from "../walletPassTypeSchemas";
import {
  BarcodeBlock,
  FieldRow,
  PassCard,
  PassHeader,
  StripWithPrimaryOverlay,
  STRIP_PRIMARY_PX,
  passInsetX,
  useColors,
  useDeviceOnly,
  useRowCertainty,
  useVisibleCounts,
} from "./sharedPreviewBits";

type Props = {
  config: WalletConfig;
  currentLanguage: string;
};

export function EventTicketPreview({ config, currentLanguage }: Props) {
  const colors = useColors(config);
  const deviceOnly = useDeviceOnly();
  const certaintyOfPrimary = useRowCertainty("primaryFields");
  const certaintyOfSecondary = useRowCertainty("secondaryFields");
  const certaintyOfAuxiliary = useRowCertainty("auxiliaryFields");
  const visible = useVisibleCounts(config);
  const hasStrip = !!config.stripImageUrl;
  return (
    <PassCard bg={colors.bg}>
      <PassHeader
        config={config}
        currentLanguage={currentLanguage}
        colors={colors}
      />

      {/* PassKit draws the single primary field ON the strip — value only, no
          label, set large across the band. This preview drew it as a labelled
          row BELOW the strip for a while, which is a layout the device never
          produces: a customer's delivered pass shows the event name written
          across the artwork with nothing underneath it.

          Only WITH artwork, though. Overlaying unconditionally put the value on
          top of the "Strip Image Area" placeholder — both boxes are `inset-0`
          and both centre their content, so the two texts collided on the same
          line in the default state of a fresh event ticket. With no strip the
          device has nothing to draw on and lays the primary out as an ordinary
          row, label and all. */}
      {hasStrip ? (
      <StripWithPrimaryOverlay
        url={config.stripImageUrl}
        fields={config.primaryFields}
        certaintyOf={certaintyOfPrimary}
        currentLanguage={currentLanguage}
        colors={colors}
        aspect={getStripAspect(config)}
        showLabel={false}
        gradient={false}
        align="center"
        // Size and weight both measured off a device, not chosen: 65pt of em
        // (the old `text-3xl` drew 35pt), and clearly not bold. See
        // STRIP_PRIMARY_PT for the pixels behind both numbers. `align="center"`
        // is confirmed by the same shot — the ink's vertical midpoint lands on
        // the band's, to within a pixel.
        valueClassName="font-normal leading-none"
        valuePx={STRIP_PRIMARY_PX}
        visibleCount={visible.primary}
      />
      ) : (
        <>
          {/* The ONLY placeholder that can still draw. This branch exists
              because a ticket with no artwork lays its primary field out as an
              ordinary row, so it cannot go through the overlay at all.
              `StripWithPrimaryOverlay` carries a copy of the same placeholder,
              and that copy is now unreachable: all three of its callers gate on
              the URL before entering it (here, `StoreCardPreview:40`,
              `CouponPreview:44`), so its `deviceOnly` arm can be mutated away
              without a single test noticing. Left in place rather than deleted
              because a fourth caller without the gate would need it — but do
              not read it as live. */}
          {!deviceOnly && (
            <div
              data-testid="wallet-strip"
              className="w-full surface-tertiary flex items-center justify-center"
              style={{ aspectRatio: getStripAspect(config) }}
            >
              <span className="body-3-regular font-tertiary">Strip Image Area</span>
            </div>
          )}
          {config.primaryFields && config.primaryFields.length > 0 && (
            <div className="pt-3 pb-1" style={passInsetX}>
              <FieldRow
                fields={config.primaryFields}
                certaintyOf={certaintyOfPrimary}
                currentLanguage={currentLanguage}
                colors={colors}
                valueClassName="text-2xl font-bold leading-tight"
                visibleCount={visible.primary}
              />
            </div>
          )}
        </>
      )}

      {config.secondaryFields && config.secondaryFields.length > 0 && (
        <div className="pb-2 pt-3" style={passInsetX}>
          <FieldRow
            fields={config.secondaryFields}
            certaintyOf={certaintyOfSecondary}
            currentLanguage={currentLanguage}
            colors={colors}
            visibleCount={visible.secondary}
          />
        </div>
      )}

      {config.auxiliaryFields && config.auxiliaryFields.length > 0 && (
        <div className="pb-2" style={passInsetX}>
          <FieldRow
            fields={config.auxiliaryFields}
            certaintyOf={certaintyOfAuxiliary}
            currentLanguage={currentLanguage}
            colors={colors}
            visibleCount={visible.auxiliary}
          />
        </div>
      )}

      <BarcodeBlock config={config} colors={colors} />
      {/* `description` is metadata (add-sheet title + VoiceOver), NEVER drawn on
          the Apple pass face — so no DescriptionBlock here. */}
    </PassCard>
  );
}
